import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  FormControl,
  Popover,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useState } from "react";

import { DimensionsPreview } from "../DimensionsPreview";
import { FormFieldIconBox } from "../FormFieldIconBox/FormFieldIconBox";
import { Icon } from "../Icon/Icon";
import { AspectRatioIcon } from "../icons";
import { LabelRow } from "../LabelRow/LabelRow";
import { BestMatchAspectRatioIcon } from "./components/BestMatchAspectRatioIcon/BestMatchAspectRatioIcon";
import { DimensionsType, PresetType } from "./types";
import { getAspectRatioRadioGroupItems, getDimensionsString } from "./utils";

export type NoneSelectedProps =
  | { supportNoneSelected?: false }
  | {
      supportNoneSelected: true;
      noneSelectedLabel: string;
      noneSelectedDimension: string;
      noneSelectedOptionLabel: string;
    };

export type AspectRatioProps = {
  value: DimensionsType | null;
  onChange: (value: DimensionsType | null) => void;
  disabled?: boolean;
  error?: boolean;
  presets?: PresetType[];
  withLabel?: boolean;
} & NoneSelectedProps;

export const AspectRatio = (props: AspectRatioProps) => {
  const {
    value,
    onChange,
    disabled = false,
    error,
    presets = [],
    withLabel = true,
    supportNoneSelected,
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const isOpen = Boolean(anchorEl);

  const foundPreset = value
    ? presets.find(({ dimensions: [w, h] }) => w === value[0] && h === value[1])
    : undefined;

  const isNoneSelected = supportNoneSelected && !value;
  const aspectRatioLabel = isNoneSelected
    ? props.noneSelectedLabel
    : foundPreset?.label || "Custom";
  const aspectRatioTitle = isNoneSelected
    ? props.noneSelectedLabel
    : foundPreset?.aspectRatioLabel || "Custom";
  const aspectRatioDimensions = isNoneSelected
    ? props.noneSelectedDimension
    : (value && getDimensionsString(value)) || "";
  const currentValue = value?.join(",") ?? null;

  return (
    <FormControl error={!!error} fullWidth>
      {withLabel && (
        <LabelRow
          useMuiLabel
          useHtmlLabel
          disabled={disabled}
          label="Aspect Ratio"
          tooltip="Choose the aspect ratio of the generated image. Layer will pick the best resolution to generate the image that works with the selected aspect ratio."
          htmlFor="aspect-ratio-label"
        />
      )}
      <Box
        sx={{
          width: "100%",
          py: "5px",
        }}
      >
        <Button
          disabled={disabled}
          fullWidth
          data-isexpanded={isOpen}
          data-testid="aspect-ratio-toggle"
          data-aspect-ratio-label={aspectRatioLabel.toLowerCase()}
          data-aspect-ratio-title={aspectRatioTitle.toLowerCase()}
          data-aspect-ratio-width={value?.[0]}
          data-aspect-ratio-height={value?.[1]}
          data-aspect-ratio-value={currentValue}
          onClick={handleOpen}
          color="secondary"
          variant="text"
          endIcon={
            <Box
              sx={{
                color: "secondary.3",
                width: "4px",
              }}
            >
              <Icon icon={faChevronRight} />
            </Box>
          }
          startIcon={
            <Box
              sx={{
                mr: 1,
                ml: 1,
              }}
            >
              {value ? (
                <FormFieldIconBox
                  icon={<AspectRatioIcon dimensions={value} />}
                />
              ) : (
                <FormFieldIconBox icon={<BestMatchAspectRatioIcon />} />
              )}
            </Box>
          }
          sx={{
            pl: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              pr: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Typography noWrap>{aspectRatioLabel}</Typography>
            <Typography
              variant="caption"
              sx={{
                color: "secondary.3",
                fontSize: 14,
              }}
            >
              {aspectRatioDimensions}
            </Typography>
          </Box>
        </Button>
      </Box>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted={true}
        disableScrollLock={true}
        slotProps={{
          paper: {
            sx: {
              width: 214,
            },
          },
        }}
      >
        <Stack spacing={0}>
          {value && <DimensionsPreview dimensions={value} />}
          <Stack
            spacing={2}
            sx={{
              padding: 2,
            }}
          >
            <RadioGroup
              value={currentValue}
              aria-labelledby="aspect-ratio-label"
              name="aspect-ratio"
            >
              {supportNoneSelected
                ? getAspectRatioRadioGroupItems({
                    value,
                    onChange,
                    presets,
                    canSelectNone: true,
                    noSelectionHeaderLabel: props.noneSelectedLabel,
                    noSelectionItemLabel: props.noneSelectedOptionLabel,
                  })
                : getAspectRatioRadioGroupItems({
                    value,
                    onChange,
                    presets,
                    canSelectNone: false,
                  })}
            </RadioGroup>
          </Stack>
        </Stack>
      </Popover>
    </FormControl>
  );
};
